import {ExternalLink, Heart, MessageCircle, Repeat2, Wand2} from "lucide-react";
import type {PostWidget} from "@/lib/chat-runtime";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {FavoriteButton} from "@/components/ui/favorite-button";
import {cn} from "@/lib/utils";
import {getPlatformInfo} from "@/lib/social-platform-utils";
import {formatMetricNumber} from "@/lib/influencers-api";
import {getSentimentBadgeClasses, getSentimentLabel} from "@/lib/sentiment-utils";
import {Link} from "react-router-dom";

interface SocialPostCardProps {
  post: PostWidget;
  /** If true, shows the widget as already favorited (filled star) */
  isFavorited?: boolean;
  /** Callback when favorite button is clicked. If not provided, favorite button is hidden. */
  onFavoriteClick?: () => void;
  /** If true, hides the Influence button (useful when already on influence page) */
  hideInfluenceButton?: boolean;
  /** Real engagement metrics when available (e.g., from InfegySentimentPost). If not provided, derived from post.engagement */
  detailedEngagement?: {
    likes: number | null;
    shares: number | null;
    comments: number | null;
  };
}

export function SocialPostCard({
  post,
  isFavorited = false,
  onFavoriteClick,
  hideInfluenceButton = false,
  detailedEngagement,
}: SocialPostCardProps) {
  const platformInfo = getPlatformInfo(post.platform);

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : null;
  const authorName = post.author_name || post.author_handle;

  return (
    <div className="relative rounded-xl border border-zinc-200 bg-white p-4 transition-all duration-200 dark:border-zinc-800 dark:bg-zinc-900/50">
      {/* Header: Favorite + Avatar + Author info */}
      {Boolean(authorName) && (
        <div className="mb-3 flex items-start gap-3">
          {/* Avatar with platform badge */}
          <div className="relative h-12 w-12 flex-shrink-0">
            <Avatar className="ring-border/50 h-full w-full ring-1">
              <AvatarImage
                src={post.author_avatar ?? undefined}
                alt={authorName}
                className="object-cover"
              />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-xs font-medium text-white">
                {authorName.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            {/* Platform badge overlay */}
            {platformInfo && (
              <div
                className={cn(
                  "absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px]",
                  platformInfo.bgClass,
                  "ring-background ring-2"
                )}
              >
                {platformInfo.emoji}
              </div>
            )}
          </div>

          {/* Author info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              {/* Favorite button */}
              {onFavoriteClick && (
                <FavoriteButton
                  isFavorited={isFavorited}
                  onClick={onFavoriteClick}
                  className="shrink-0"
                />
              )}
              <p className="text-foreground truncate text-base font-semibold tracking-tight">
                {authorName}
              </p>
            </div>
            <p className="text-muted-foreground truncate text-sm">
              @{post.author_handle.replace(/^@/, "")}
              {formattedDate && ` · ${formattedDate}`}
            </p>
          </div>

          {/* Sentiment badge */}
          {post.sentiment && (
            <Badge
              variant="outline"
              className={cn(
                "shrink-0 text-xs font-medium",
                getSentimentBadgeClasses(post.sentiment)
              )}
            >
              {getSentimentLabel(post.sentiment)}
            </Badge>
          )}
        </div>
      )}

      {/* Post text - full width */}
      <p className="text-[15px] leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap text-zinc-800 dark:text-zinc-200">
        {post.text}
      </p>

      {/* Engagement bar - full width */}
      <div className="mt-3 flex items-center justify-between gap-6 text-zinc-500 dark:text-zinc-400">
        <div className="flex items-center gap-3">
          {/* Engagement metrics - use real values if available, otherwise derive from total */}
          <div className="group/stat flex items-center gap-1.5 text-xs transition-colors hover:text-blue-500">
            <MessageCircle className="h-4 w-4" />
            <span>
              {formatMetricNumber(
                detailedEngagement?.comments ?? Math.floor((post.engagement || 100) * 0.1)
              )}
            </span>
          </div>
          <div className="group/stat flex items-center gap-1.5 text-xs transition-colors hover:text-green-500">
            <Repeat2 className="h-4 w-4" />
            <span>
              {formatMetricNumber(
                detailedEngagement?.shares ?? Math.floor((post.engagement || 100) * 0.3)
              )}
            </span>
          </div>
          <div className="group/stat flex items-center gap-1.5 text-xs transition-colors hover:text-rose-500">
            <Heart className="h-4 w-4" />
            <span>{formatMetricNumber(detailedEngagement?.likes ?? post.engagement ?? 0)}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Influence button */}
          {!hideInfluenceButton && (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="h-7 cursor-pointer gap-1 bg-violet-500/10 px-2 text-violet-600 hover:bg-violet-500/20 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
            >
              <Link to={`/post/${post.id}`}>
                <Wand2 className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Influence</span>
              </Link>
            </Button>
          )}

          {/* View button */}
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-7 cursor-pointer gap-1 px-2 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            <a href={post.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3 w-3" />
              <span className="text-xs">View</span>
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
